/**
 * Dev-auth token provider.
 *
 * Oxbo-M requires `Authorization: Bearer <JWT>` on every data call, and the
 * dev-minted JWT expires in ~15 minutes. This module mints a token from the
 * auth service's `_dev/mint` endpoint, holds it in memory for the mApi
 * baseQuery to read, and proactively refreshes it ~2 minutes before expiry.
 *
 * It is gated entirely behind `VITE_DEV_AUTH=true`. In a future real-login
 * prod build (`VITE_DEV_AUTH` unset/false) none of this runs — the mApi
 * baseQuery simply finds no token and sends no Authorization header, leaving
 * room for a real auth flow.
 *
 * Oxbo-P (pApi) needs no token in dev, so it never consults this module.
 */

/** Demo org/user the dev mint issues a token for. */
const DEV_ORG_ID = "00000000-0000-0000-0000-0000000000a0";
const DEV_USER_ID = "00000000-0000-0000-0000-0000000000d0";
const DEV_ROLES = ["owner", "super_admin"];

/** Refresh this many ms before the token's expiry so a call never races exp. */
const REFRESH_LEAD_MS = 2 * 60 * 1000; // 2 min
/** Fallback lifetime if the token carries no readable exp (dev mint says ~15m). */
const FALLBACK_LIFETIME_MS = 15 * 60 * 1000;
/** Assume at least this much runway so a bad clock can't schedule exp in the past. */
const MIN_LIFETIME_MS = 60 * 1000;
/** Back-off before retrying a failed mint. */
const RETRY_DELAY_MS = 5 * 1000;

/** Whether dev-auth is active for this build. */
export function isDevAuthEnabled(): boolean {
  return import.meta.env.VITE_DEV_AUTH === "true";
}

function mintUrl(): string {
  const base = import.meta.env.VITE_OXBO_AUTH_URL ?? "/api/auth";
  return `${base}/v1/auth/_dev/mint`;
}

let currentToken: string | null = null;
let refreshTimer: ReturnType<typeof setTimeout> | null = null;
let inFlight: Promise<string | null> | null = null;

/** The current dev token, or null if none has been minted yet. */
export function getDevToken(): string | null {
  return currentToken;
}

/**
 * The current user's id — read from the dev token's `sub` claim, falling back
 * to the demo dev user when no readable token is present. Consumers (e.g. the
 * Saved Views "Mine" filter, which compares against `owner_user_id`) need to
 * know who "me" is; there is no user slice yet, so this is the single source.
 */
export function getCurrentUserId(): string {
  if (currentToken) {
    try {
      const [, payload] = currentToken.split(".");
      if (payload) {
        const json = atob(payload.replace(/-/g, "+").replace(/_/g, "/"));
        const claims = JSON.parse(json) as { sub?: string };
        if (typeof claims.sub === "string" && claims.sub) return claims.sub;
      }
    } catch {
      // fall through to the dev id
    }
  }
  return DEV_USER_ID;
}

/** Decode a JWT's `exp` (seconds) → absolute ms, or null if unreadable. */
function readExpMs(token: string): number | null {
  try {
    const [, payload] = token.split(".");
    if (!payload) return null;
    const json = atob(payload.replace(/-/g, "+").replace(/_/g, "/"));
    const claims = JSON.parse(json) as { exp?: number };
    if (typeof claims.exp !== "number") return null;
    return claims.exp * 1000;
  } catch {
    return null;
  }
}

/** Mint a single token from the auth service. Throws on any non-ok response. */
async function mintOnce(): Promise<string> {
  const res = await fetch(mintUrl(), {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({
      org_id: DEV_ORG_ID,
      user_id: DEV_USER_ID,
      roles: DEV_ROLES,
    }),
  });
  if (!res.ok) {
    throw new Error(`dev mint failed: ${res.status} ${res.statusText}`);
  }
  const body = (await res.json()) as { access_token?: string };
  if (!body.access_token) {
    throw new Error("dev mint returned no access_token");
  }
  return body.access_token;
}

/** Schedule the next refresh a safe margin before the token expires. */
function scheduleRefresh(token: string): void {
  if (refreshTimer) clearTimeout(refreshTimer);
  const expMs = readExpMs(token);
  const lifetime = expMs ? expMs - Date.now() : FALLBACK_LIFETIME_MS;
  const delay = Math.max(MIN_LIFETIME_MS, lifetime - REFRESH_LEAD_MS);
  refreshTimer = setTimeout(() => {
    void refreshDevToken();
  }, delay);
}

/**
 * Mint (or re-mint) the dev token and store it. Retries on failure with a
 * short back-off, so a transient auth-service blip never leaves the app
 * permanently token-less. Concurrent callers share one in-flight mint.
 */
export function refreshDevToken(): Promise<string | null> {
  if (inFlight) return inFlight;
  inFlight = (async () => {
    try {
      const token = await mintOnce();
      currentToken = token;
      scheduleRefresh(token);
      return token;
    } catch (err) {
      // Keep any previously-valid token; retry shortly.
      if (typeof console !== "undefined") {
        console.warn("[devAuth] mint failed, retrying", err);
      }
      if (refreshTimer) clearTimeout(refreshTimer);
      refreshTimer = setTimeout(() => {
        void refreshDevToken();
      }, RETRY_DELAY_MS);
      return currentToken;
    } finally {
      inFlight = null;
    }
  })();
  return inFlight;
}

/**
 * Initialize dev auth at app start: mint the first token (so the initial
 * render already has one) and arm the refresh loop. No-op unless dev-auth is
 * enabled. Never throws — a failed initial mint schedules a retry.
 */
export async function initDevAuth(): Promise<void> {
  if (!isDevAuthEnabled()) return;
  await refreshDevToken();
}

/** Test-only: clear all in-memory state and timers. */
export function __resetDevAuthForTests(): void {
  currentToken = null;
  inFlight = null;
  if (refreshTimer) {
    clearTimeout(refreshTimer);
    refreshTimer = null;
  }
}
